import { X } from 'lucide-react';
import SignaturePad from 'react-signature-canvas';
import { useEffect, useRef, useState } from 'react';
import { createSignature } from '../../services/jobcardApi';
import { Signature } from '../../types/jobcard';
import { useAuth } from '../../hooks/useAuth';

interface Props {
    jobCardId: number;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    existingSignatures?: Signature[];
    isStoreUser?: boolean;
}

export default function CreateSignatureModal({ jobCardId, isOpen, onClose, onSuccess, existingSignatures = [], isStoreUser = false }: Props) {
    const { user } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [isManager, setIsManager] = useState(false);
    const signaturePadRef = useRef<SignaturePad>(null);
    const [saving, setSaving] = useState(false);

    // Determine which signature types are already present
    const hasTechnicianOrStoreManager = existingSignatures.some(sig => !sig.is_manager);
    const hasManagerOrSupplier = existingSignatures.some(sig => sig.is_manager);

    // Set default signature type and name based on what's missing
    useEffect(() => {
        if (isStoreUser) {
            // For store users, default to whichever is missing, and never pre-fill the name.
            if (!hasTechnicianOrStoreManager) {
                setIsManager(false);
            } else if (!hasManagerOrSupplier) {
                setIsManager(true);
            }
            setName(''); // Always start with an empty name for manual entry.
        } else {
            // For regular users (technicians)
            if (!hasTechnicianOrStoreManager) {
                setIsManager(false);
                setName(user?.name || '');
            } else if (!hasManagerOrSupplier) {
                setIsManager(true);
                setName('');
            }
        }
    }, [hasTechnicianOrStoreManager, hasManagerOrSupplier, user?.name, isStoreUser, isOpen]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSaving(true);

        // Ensure signature pad is not empty and exists
        if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
            try {
                const signatureData = signaturePadRef.current.toDataURL();
                if (!signatureData) {
                    setError('Please draw a signature first');
                    setSaving(false);
                    return;
                }
                console.log('Submitting signature with isManager:', isManager);
                await createSignature(jobCardId, {
                    signatures: signatureData,
                    name,
                    is_manager: isManager
                });
                onSuccess();
                // Reset form
                setName('');
                signaturePadRef.current.clear();
            } catch (err) {
                setError('Failed to save signature');
                console.error(err);
            }
        } else {
            setError('Please provide a signature');
        }
        setSaving(false);
    };

    const clearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
    };

    // Prevent signature pad from disappearing on mobile
    const preventTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        // Prevent default scrolling behavior
        e.preventDefault();
    };

    // Re-enable scrolling when modal closes
    useEffect(() => {
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    // Add touch event handling for signature pad
    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        // Stop event propagation to prevent interference
        e.stopPropagation();
    };

    // Manage body class when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }

        // Cleanup
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Signature</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200">
                        <X size={24} />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                            Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full p-2 border dark:border-gray-600 rounded-lg transition-colors duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-1 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                            required
                            placeholder="Enter name"
                        />
                        {/* Clear labels for store users */}
                        {isStoreUser && (
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                {!isManager ? (
                                    <p className="text-blue-600 dark:text-blue-400">📋 <strong>Store Manager:</strong> Enter the name of the store manager who supervised the work</p>
                                ) : (
                                    <p className="text-green-600 dark:text-green-400">🏢 <strong>Supplier Representative:</strong> Enter the name of the external vendor representative</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                            Type
                        </label>
                        <div className="flex gap-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    checked={!isManager}
                                    onChange={() => {
                                        setIsManager(false);
                                        // For regular users, pre-fill name; for store users, ensure it's manual
                                        setName(isStoreUser ? '' : user?.name || '');
                                    }}
                                    disabled={hasTechnicianOrStoreManager}
                                    className="form-radio h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600"
                                />
                                <span className="ml-2 text-gray-700 dark:text-gray-300">
                                    {isStoreUser ? 'Manager' : 'Technician'}
                                </span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    checked={isManager}
                                    onChange={() => {
                                        setIsManager(true);
                                        setName(''); // Always clear name for the second signature type
                                    }}
                                    disabled={hasManagerOrSupplier}
                                    className="form-radio h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600"
                                />
                                <span className="ml-2 text-gray-700 dark:text-gray-300">
                                    {isStoreUser ? 'Supplier' : 'Manager'}
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                            Signature
                        </label>
                        <div
                            className="signature-pad-container border dark:border-gray-600 rounded-lg p-2 bg-white w-full overflow-hidden"
                            onTouchMove={preventTouchMove}
                            onTouchStart={handleTouchStart}
                        >
                            <SignaturePad
                                ref={signaturePadRef}
                                canvasProps={{
                                    className: 'signature-canvas w-full',
                                    width: 320,
                                    height: 200,
                                    style: {
                                        background: 'white',
                                        width: '100%',
                                        height: '200px'
                                    }
                                }}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={clearSignature}
                            className="mt-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                        >
                            Clear Signature
                        </button>
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 text-white bg-indigo-600 dark:bg-indigo-500 rounded-lg transition-colors duration-200 ${saving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700 dark:hover:bg-indigo-600'}`}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
} 